/**
 * E-posta Hesaplarım — a staff member's OWN mail accounts (backlog #574, backend /email/my-accounts).
 *
 * Any staff login opens it (route with no permission; the profile menu links to it). The member adds, edits, deletes and tests only their own
 * accounts: at most MAX_HESAP of them, each with a server (a host NAME on a public address, port 25 / 465 / 587 / 2525; the backend refuses an
 * IP address or an internal host and says why), a login and a password. The password is write-only: it is stored encrypted, no page and no
 * administrator can read it back, and a blank password on an edit keeps the stored one; changing the server, port, security or SMTP user
 * needs it typed again (Save is closed with a warning until then, the backend refuses it too).
 * "Send me a test" sends one small mail through the account to the member's OWN address (SENT / BLOCKED by safe mode / FAILED with the reason);
 * ten tests an hour are allowed. A personal account sends nothing else yet.
 *
 * The presets (Yandex, Gmail app password, Office 365, other SMTP) only fill the server fields and set the account type; they are not a
 * catalogue in the database.
 */
import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, CardBody, Container, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../../../Components/Common/BreadCrumb";
import i18n from "../../../../../i18n";
import SmtpAlanlari from "../Configs/SmtpAlanlari";
import { createMyAccount, deleteMyAccount, listMyAccounts, testMyAccount, updateMyAccount } from "../../../../../services/emailAyarService";

export const MAX_HESAP = 3;

/** Account type ids (the backend accepts 1-4) and what each preset fills in. */
export const TURLER = { yandex: 1, gmail: 2, office365: 3, diger: 4 };
export const HAZIR = {
    yandex: { smtp_host: "smtp.yandex.com", smtp_port: 465, smtp_security: "SSL" },
    gmail: { smtp_host: "smtp.gmail.com", smtp_port: 465, smtp_security: "SSL" },
    office365: { smtp_host: "smtp.office365.com", smtp_port: 587, smtp_security: "STARTTLS" },
    diger: {},
};

const BOS_FORM = {
    system_email_title: "", system_email_value: "", system_email_password: "", type_id: TURLER.diger,
    smtp_host: "", smtp_port: "", smtp_security: "", smtp_username: "", smtp_from_name: "",
};
const FORM_ALANLARI = Object.keys(BOS_FORM);
// Where the account logs in: if one of these changes, the password has to be typed again.
const HEDEF_ALANLARI = ["smtp_host", "smtp_port", "smtp_security", "smtp_username"];
const norm = (k, v) => {
    const s = v === null || v === undefined ? "" : String(v).trim();
    return k === "smtp_host" || k === "smtp_security" ? s.toLowerCase() : s;
};
const turAnahtari = (id) => Object.keys(TURLER).find((k) => TURLER[k] === id) || "diger";

const MyAccounts = () => {
    const { t } = useTranslation("moduller");
    useEffect(() => { document.title = i18n.t("moduller:emailsBenim.baslikFds"); }, []);

    const [hesaplar, setHesaplar] = useState([]);
    const [yukleniyor, setYukleniyor] = useState(true);
    const [hata, setHata] = useState(null);
    const [yenile, setYenile] = useState(0);
    const [form, setForm] = useState(null);                // null = closed; { id (null = new), deger, orig }
    const [kaydediyor, setKaydediyor] = useState(false);
    const [formHatasi, setFormHatasi] = useState(null);
    const [silinecek, setSilinecek] = useState(null);
    const [siliniyor, setSiliniyor] = useState(false);
    const [testSonucu, setTestSonucu] = useState(null);
    const [testIsteniyor, setTestIsteniyor] = useState(null);

    // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
    useEffect(() => {
        let ignore = false;
        async function run() {
            try {
                const d = await listMyAccounts();
                if (!ignore) { setHesaplar(d); setHata(null); }
            } catch (e) {
                if (!ignore) setHata(e.message);
            } finally {
                if (!ignore) setYukleniyor(false);
            }
        }
        run();
        return () => { ignore = true; };
    }, [yenile]);

    const doldu = hesaplar.length >= MAX_HESAP;

    function yeniAc() {
        setFormHatasi(null);
        setForm({ id: null, deger: { ...BOS_FORM }, orig: null });
    }

    function duzenleAc(h) {
        const deger = {};
        FORM_ALANLARI.forEach((k) => { deger[k] = h[k] ?? BOS_FORM[k]; });
        deger.system_email_password = "";
        setFormHatasi(null);
        setForm({ id: h.system_email_id, deger, orig: h });
    }

    function degistir(patch) {
        setForm((f) => ({ ...f, deger: { ...f.deger, ...patch } }));
    }

    function hazirSec(anahtar) {
        degistir({ type_id: TURLER[anahtar], ...HAZIR[anahtar] });
    }

    const hedefDegisti = Boolean(form && form.id && form.orig?.has_password
        && HEDEF_ALANLARI.some((k) => norm(k, form.orig[k]) !== norm(k, form.deger[k])));
    const sifreGerekli = hedefDegisti && !form.deger.system_email_password;

    async function kaydet() {
        setKaydediyor(true);
        setFormHatasi(null);
        try {
            const gonder = { ...form.deger, smtp_port: form.deger.smtp_port === "" ? "" : Number(form.deger.smtp_port) };
            if (!gonder.system_email_password) delete gonder.system_email_password;   // blank = keep the stored one
            if (form.id) await updateMyAccount(form.id, gonder); else await createMyAccount(gonder);
            setForm(null);
            setYenile((n) => n + 1);
        } catch (e) {
            setFormHatasi(e.message);   // the backend's own text: the limit, the server rules, the password again
        } finally {
            setKaydediyor(false);
        }
    }

    async function sil() {
        setSiliniyor(true);
        try {
            await deleteMyAccount(silinecek.system_email_id);
            setSilinecek(null);
            setYenile((n) => n + 1);
        } catch (e) {
            setSilinecek(null);
            setHata(e.message);
        } finally {
            setSiliniyor(false);
        }
    }

    async function testEt(h) {
        setTestIsteniyor(h.system_email_id);
        setTestSonucu(null);
        try {
            const r = await testMyAccount(h.system_email_id);
            setTestSonucu({ hesap: h.system_email_title, status: r.status, message: r.message, error: r.error });
        } catch (e) {
            setTestSonucu({ hesap: h.system_email_title, status: "ERROR", message: e.message, error: null });   // 429: too many tests
        } finally {
            setTestIsteniyor(null);
        }
    }
    const testRengi = (s) => (s === "SENT" ? "success" : s === "BLOCKED" ? "warning" : "danger");

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title={t("emailsBenim.baslik")} pageTitle={t("emailsConfigs.emails")} />
                <Card>
                    <CardBody>
                        <p className="text-muted mb-2">{t("emailsBenim.aciklama")}</p>
                        <p className="text-muted small mb-3">{t("emailsBenim.sifreGuvenligi")}</p>
                        <Button color="primary" disabled={doldu || yukleniyor} onClick={yeniAc}>{t("emailsBenim.ekle")}</Button>
                        {doldu && <span className="ms-3 small text-muted">{t("emailsBenim.sinir", { adet: MAX_HESAP })}</span>}
                    </CardBody>
                </Card>

                {hata && <Alert color="danger" className="py-2" toggle={() => setHata(null)}>{hata}</Alert>}
                {testSonucu && (
                    <Alert color={testRengi(testSonucu.status)} className="py-2" toggle={() => setTestSonucu(null)}>
                        <strong>{t("emailsConfigs.testSonucu", { hesap: testSonucu.hesap })}: </strong>{testSonucu.message}
                        {testSonucu.error && <span className="d-block small">{testSonucu.error}</span>}
                    </Alert>
                )}

                <Card className="pe-3 ps-3 pt-3">
                    {yukleniyor ? (
                        <div className="pb-3"><Spinner size="sm" className="me-2" />{t("common:yukleniyor")}</div>
                    ) : hesaplar.length === 0 ? (
                        <p className="text-muted">{t("emailsBenim.yok")}</p>
                    ) : (
                        <Table responsive bordered striped>
                            <thead>
                                <tr>
                                    <th>{t("emailsConfigs.baslik")}</th>
                                    <th>{t("emailsConfigs.email")}</th>
                                    <th>{t("emailsConfigs.smtpSunucu")}</th>
                                    <th>{t("emailsConfigs.sifre")}</th>
                                    <th>{t("emailsConfigs.islem")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {hesaplar.map((h) => (
                                    <tr key={h.system_email_id}>
                                        <td>{h.system_email_title}</td>
                                        <td>{h.system_email_value}</td>
                                        <td>{h.smtp_host ? `${h.smtp_host}${h.smtp_port ? `:${h.smtp_port}` : ""}${h.smtp_security ? ` · ${h.smtp_security}` : ""}` : "—"}</td>
                                        <td>
                                            {!h.has_password
                                                ? <Badge color="secondary">{t("emailsConfigs.yok")}</Badge>
                                                : h.password_encrypted
                                                    ? <Badge color="success">{t("emailsConfigs.sifreSifreli")}</Badge>
                                                    : <Badge color="warning" title={t("emailsConfigs.sifreDuzMetinAciklama")}>{t("emailsConfigs.sifreDuzMetin")}</Badge>}
                                        </td>
                                        <td className="text-nowrap">
                                            <Button color="primary" outline className="me-2" disabled={testIsteniyor !== null} onClick={() => testEt(h)}>
                                                {testIsteniyor === h.system_email_id ? <Spinner size="sm" /> : t("emailsConfigs.testGonder")}
                                            </Button>
                                            <Button color="warning" className="me-2" onClick={() => duzenleAc(h)}>{t("common:duzenle")}</Button>
                                            <Button color="primary" onClick={() => setSilinecek(h)}>{t("common:sil")}</Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card>

                {/* add / edit */}
                <Modal isOpen={Boolean(form)} toggle={() => !kaydediyor && setForm(null)} size="lg" scrollable>
                    <ModalHeader toggle={() => !kaydediyor && setForm(null)}>{form?.id ? t("emailsBenim.duzenleBaslik") : t("emailsBenim.ekleBaslik")}</ModalHeader>
                    <ModalBody>
                        {form && (
                            <>
                                {formHatasi && <Alert color="danger" className="py-2">{formHatasi}</Alert>}
                                <Label className="form-label" htmlFor="benim-hazir">{t("emailsBenim.tur")}</Label>
                                <Input id="benim-hazir" className="mb-1" type="select" value={turAnahtari(form.deger.type_id)} onChange={(e) => hazirSec(e.target.value)}>
                                    {Object.keys(TURLER).map((k) => <option key={k} value={k}>{t(`emailsBenim.tur_${k}`)}</option>)}
                                </Input>
                                <p className="small text-muted mb-3">{t(`emailsBenim.ipucu_${turAnahtari(form.deger.type_id)}`)}</p>
                                <Input className="mb-3" type="text" placeholder={t("emailsConfigs.baslik")}
                                    value={form.deger.system_email_title} onChange={(e) => degistir({ system_email_title: e.target.value })} />
                                <Input className="mb-3" type="email" placeholder={t("emailsConfigs.emailAdresi")}
                                    value={form.deger.system_email_value} onChange={(e) => degistir({ system_email_value: e.target.value })} />
                                <Input className="mb-1" type="password" autoComplete="new-password"
                                    placeholder={form.orig?.has_password ? t("emailsConfigs.yeniSifreBosDegismez") : t("emailsConfigs.sifre")}
                                    value={form.deger.system_email_password} onChange={(e) => degistir({ system_email_password: e.target.value })} />
                                <p className="small text-muted mb-3">{t("emailsBenim.sifreGuvenligi")}</p>
                                {sifreGerekli && <Alert color="warning" className="py-2 small">{t("emailsConfigs.sifreHedefDegisti")}</Alert>}
                                <SmtpAlanlari deger={form.deger} degistir={degistir} varsayilanGizle ipucuGizle />
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" disabled={kaydediyor || sifreGerekli} onClick={kaydet}>
                            {kaydediyor ? <Spinner size="sm" /> : (form?.id ? t("common:kaydet") : t("common:ekle"))}
                        </Button>
                        <Button color="light" disabled={kaydediyor} onClick={() => setForm(null)}>{t("common:iptal")}</Button>
                    </ModalFooter>
                </Modal>

                {/* delete */}
                <Modal isOpen={Boolean(silinecek)} toggle={() => !siliniyor && setSilinecek(null)}>
                    <ModalHeader toggle={() => !siliniyor && setSilinecek(null)}>{t("emailsConfigs.onay")}</ModalHeader>
                    <ModalBody>{silinecek ? t("emailsBenim.silOnay", { hesap: silinecek.system_email_title }) : ""}</ModalBody>
                    <ModalFooter>
                        <Button color="primary" disabled={siliniyor} onClick={sil}>{siliniyor ? <Spinner size="sm" /> : t("common:sil")}</Button>
                        <Button color="light" disabled={siliniyor} onClick={() => setSilinecek(null)}>{t("common:iptal")}</Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default MyAccounts;
